'use client'
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'

const BannerContext = createContext()

// Reducer function
const bannerReducer = (state, action) => {
  switch (action.type) {
    case 'SHOW':
      return { ...state, isBannerVisible: true }
    case 'HIDE':
      return { ...state, isBannerVisible: false }
    default:
      return state
  }
}

// Initial state
const initialState = {
  isBannerVisible: true,
}

export const BANNER_EVENTS = {
  SHOW: 'banner:show',
  HIDE: 'banner:hide',
}

export const emitBannerEvent = (eventType) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventType))
  }
}

const BannerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bannerReducer, initialState)

  const showBanner = useCallback(() => dispatch({ type: 'SHOW' }), [])
  const hideBanner = useCallback(() => dispatch({ type: 'HIDE' }), [])

  useEffect(() => {
    const handleShow = () => showBanner()
    const handleHide = () => hideBanner()

    window.addEventListener(BANNER_EVENTS.SHOW, handleShow)
    window.addEventListener(BANNER_EVENTS.HIDE, handleHide)

    return () => {
      window.removeEventListener(BANNER_EVENTS.SHOW, handleShow)
      window.removeEventListener(BANNER_EVENTS.HIDE, handleHide)
    }
  }, [showBanner, hideBanner])

  const contextValue = useMemo(
    () => ({
      isBannerVisible: state.isBannerVisible,
      showBanner,
      hideBanner,
    }),
    [state.isBannerVisible, showBanner, hideBanner]
  )

  return <BannerContext.Provider value={contextValue}>{children}</BannerContext.Provider>
}

const useBanner = () => {
  const context = useContext(BannerContext)
  if (!context) {
    throw new Error('useBanner must be used within a BannerProvider')
  }
  return context
}

export { BannerProvider, useBanner }
